import { useEffect, useRef, type ReactNode } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { LW_DS_CLASS } from './LwThemeScope';
import { useScrollLock, useWindowEscapeFallback } from './hooks';

/**
 * Right slide-over drawer shell (scrim + `.drawer`). Compose `.dw-head`,
 * `.dw-tabbar`, `.dw-body`, and `.dw-foot` inside — or use the `LwDrawerHeader`
 * helper for the standard avatar + serif title + close header.
 */
export function LwDrawer({ onClose, label, className, children }: { onClose: () => void; label?: string; className?: string; children: ReactNode }) {
  useScrollLock();
  const contentRef = useRef<HTMLElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(
    typeof document !== 'undefined' && document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null
  );
  useWindowEscapeFallback(contentRef, onClose);
  useEffect(() => () => {
    const returnTarget = returnFocusRef.current;
    if (returnTarget && document.contains(returnTarget)) {
      returnTarget.focus();
    }
  }, []);

  return (
    <DialogPrimitive.Root
      open
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogPrimitive.Portal>
        <div className={LW_DS_CLASS}>
          <DialogPrimitive.Overlay asChild>
            <div className="scrim" onClick={onClose} />
          </DialogPrimitive.Overlay>
          <DialogPrimitive.Content
            asChild
            id={undefined}
            aria-label={label}
            aria-describedby={undefined}
            onOpenAutoFocus={(event) => {
              event.preventDefault();
              contentRef.current?.focus();
            }}
            onCloseAutoFocus={(event) => {
              event.preventDefault();
              const returnTarget = returnFocusRef.current;
              if (returnTarget && document.contains(returnTarget)) {
                returnTarget.focus();
              }
            }}
            onPointerDownOutside={(event) => event.preventDefault()}
          >
            <aside ref={contentRef} tabIndex={-1} className={`drawer${className ? ` ${className}` : ''}`} data-lw-overlay-layer="">
              {label && <DialogPrimitive.Title className="sr-only">{label}</DialogPrimitive.Title>}
              {children}
            </aside>
          </DialogPrimitive.Content>
        </div>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

/** Standard drawer header: an avatar slot + serif name + muted sub + close button. */
export function LwDrawerHeader({
  avatar,
  name,
  sub,
  onClose
}: {
  avatar?: ReactNode;
  name: ReactNode;
  sub?: ReactNode;
  onClose: () => void;
}) {
  return (
    <div className="dw-head">
      {avatar}
      <div className="dw-id">
        <div className="dw-name">{name}</div>
        {sub && <div className="dw-sub">{sub}</div>}
      </div>
      <button className="dw-close" onClick={onClose} aria-label="Close" type="button">
        <X />
      </button>
    </div>
  );
}
